import React, { forwardRef } from 'react';
import { JcCommonProps, JcSize, JcUtils } from '../../utils/JcUtils';

export enum JcButtonType {
  PRIMARY = "primary",
  DEFAULT = "default",
  GHOST = "ghost",
  DELETE = "delete",
}

/**
 * The visual appearance of a button. Shared by anything that should *look* like
 * a button regardless of the underlying element — a real `<button>` (JcButton)
 * or an `<a>` styled as a button (JcLink / JcRouteLink). Keeping the look in one
 * place lets the semantic element be chosen by behaviour (action vs navigation)
 * without duplicating styling.
 */
export interface JcButtonAppearance {
  readonly selected?: boolean;
  readonly disabled?: boolean;
  readonly buttonType?: JcButtonType;
  /** Defaults to `JcSize.MEDIUM`. */
  readonly size?: JcSize.EXTRA_SMALL | JcSize.SMALL | JcSize.MEDIUM | JcSize.LARGE | JcSize.EXTRA_LARGE;
}

/**
 * Computes the class list for a given button appearance. `clickable` controls the
 * `jc-clickable` affordance (hover/cursor) — pass false for disabled controls.
 */
export function jcButtonAppearanceClasses(
  appearance: JcButtonAppearance,
  clickable: boolean
): (string | false | null | undefined)[] {
  const buttonType = appearance.buttonType ?? JcButtonType.DEFAULT;
  const size = appearance.size ?? JcSize.MEDIUM;
  return [
    appearance.selected && "jc-selected",
    clickable && "jc-clickable",
    appearance.disabled && "jc-disabled",
    "jc-button-" + buttonType,
    "jc-button-size-" + size,
    "jc-button",
  ];
}

export interface JcButtonProps extends JcCommonProps, JcButtonAppearance {
  readonly onClick?: () => void;
  /** Accessible name — required when `children` doesn't convey one (e.g. an icon-only button). */
  readonly ariaLabel?: string;
  /** Toggle state (`aria-pressed`) for a button acting as an on/off choice. */
  readonly ariaPressed?: boolean;
  /** Disclosure state (`aria-expanded`) for a button that reveals content. */
  readonly ariaExpanded?: boolean;
  readonly children: React.ReactNode;
}

export const JcButton = forwardRef((props: JcButtonProps, ref: React.ForwardedRef<HTMLButtonElement>): React.ReactElement => {

  const onClick = props.disabled ? undefined : props.onClick;
  const commonProps = JcUtils.commonProps(props, jcButtonAppearanceClasses(props, !!onClick));

  return <button
    ref={ref}
    {...commonProps}
    type="button"
    onClick={onClick}
    disabled={props.disabled}
    aria-label={props.ariaLabel}
    aria-pressed={props.ariaPressed}
    aria-expanded={props.ariaExpanded}
  >
    {props.children}
  </button>
})
